import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kMatterArtifactTemplateSelectionApiPath } from '@/agent-v2/artifact-templates/constants'
import { MatterArtifactTemplateSelectionReadSchema, type MatterArtifactTemplateSelectionRead } from '@/artifact-templates/schemas'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/**
 * Persist (or replace) a Matter artifact's sticky template selection. The backend treats the
 * selection as a singleton per Matter artifact, so this is idempotent. Returns the persisted
 * selection record — callers reflect the response (server as source of truth) into the store.
 */
export async function putMatterArtifactTemplateSelection(matterArtifactId: string, templateUserFileId: string): Promise<MatterArtifactTemplateSelectionRead> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMatterArtifactTemplateSelectionApiPath(matterArtifactId)}`
    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentry_transaction_id,
      },
      body: JSON.stringify({ template_user_file_id: templateUserFileId }),
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = (parseError ? {} : data) as { detail?: unknown }
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(
          new Error(`Save Matter artifact template selection failed - status: ${response.status}, detail: ${JSON.stringify(errorData.detail)}`),
          {
            extra: {
              ...getResponseDebugContext(response, apiUrl),
              matterArtifactId,
              templateUserFileId,
              responseData: errorData,
              parseError: parseError ? formatUnknownError(parseError) : null,
            },
          }
        )
      })
      throw new ApiError(response.status, `Failed to save template selection: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Save Matter artifact template selection response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterArtifactId,
            templateUserFileId,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid template selection response JSON')
    }

    const parsed = MatterArtifactTemplateSelectionReadSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Save Matter artifact template selection response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterArtifactId,
            templateUserFileId,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid template selection response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
